import { effect } from '@preact/signals';
import { isEqual } from 'es-toolkit';
import toast from 'react-hot-toast';
import {
  currentDuration,
  currentEpisode,
  currentEpisodeId,
  currentPlaylist,
  currentSeek,
  getRequestedState,
  getState,
  sleepTimer,
  state,
} from '../global-state/audio-signals';
import { DownloadsStore } from '../global-state/downloads';
import { markUpcomingEpisodesJustSyncedToNative } from '../global-state/native-app-state';
import {
  idbStartupPromise,
  idToEpisode,
  usePodcastsStore,
} from '../global-state/podcasts';
import { wasAdRecentlySkipped } from '../global-state/session';
import {
  determineCredits,
  pollForPodcastEveryMinute,
  syncUsageDataNow,
  totalAdSkipsLeft,
  trackAdSkipUnlessRecent,
} from '../global-state/usage-data';
import { currentUserId } from '../global-state/user';
import { UserDataStore } from '../global-state/user-data';
import { AudioState, SleepTimer } from '../models/audio-state';
import { Episode, EpisodeCompoundId, Podcast } from '../models/podcasts';
import { objectEntries, objectFromEntries } from '../utils/ts-extras';
import log from './debug';
import { reportSentryError, reportSentryWarning } from './error-handling';
import getThumbUrl from './get-thumb';
import { exposeOnWindow, getEpisodeDuration, isWithin, waitForEvent } from './helpers';
import { isNativeAppWithStateV1 } from './platform';

const sendMessage = (message: { command: string; [key: string]: unknown }) => {
  try {
    // Note: if the message triggers a Java exception, it will throw a JS error
    window.Android!.sendMessage(JSON.stringify(message));
  } catch (error) {
    console.log('Android sendMessage failed', message);
    reportSentryError(error as Error);
  }
};

type UampMediaItem = {
  id: string;
  title: string;
  album: string;
  artist: string;
  genre: string;
  source: string;
  image: string;
  trackNumber: number;
  totalTrackCount: number;
  duration: number;
  site: string;
};

type NativePlaylistEpisode = {
  id: EpisodeCompoundId;
  position: number;
  ads: {
    startTime: number;
    endTime: number;
    skipCostCredits: number;
    wasRecentlySkipped: boolean;
  }[];
  jsonPlaylist: string;
};

class AndroidAudioPlayer {
  constructor() {
    log('Android iOS Audio Player');

    sendMessage({ command: 'readyForEvents' });

    this.setupUserIdSync();

    addEventListener('UPDATE_SAFE_AREAS', ((
      event: CustomEvent<{ bottom: number; top: number; sdkVersionInt: number }>
    ) => {
      const androidSafeAreas = event.detail;
      const getCssVar = (name: string) =>
        getComputedStyle(document.documentElement).getPropertyValue(name);
      const cssSafeAreas = {
        bottom: parseInt(getCssVar('--safe-area-bottom')),
        top: parseInt(getCssVar('--safe-area-top')),
      };
      log(
        'Got Android & CSS safe areas',
        JSON.stringify({ android: androidSafeAreas, css: cssSafeAreas })
      );

      // Edge-to-edge is only enabled by default in Android 35+
      if (androidSafeAreas.sdkVersionInt < 35) return;

      if (androidSafeAreas.bottom && cssSafeAreas.bottom === 0) {
        const offset = Math.min(androidSafeAreas.bottom, 48); // 48px is a lot, don't go over that
        log("Override safe area bottom since it's not set in CSS", offset);
        document.documentElement.style.setProperty('--safe-area-bottom', offset + 'px');
      }
      if (androidSafeAreas.top && cssSafeAreas.top === 0) {
        const offset = Math.min(androidSafeAreas.top, 32); // 32px should be plenty for a notch
        log("Override safe area top since it's not set in CSS", offset);
        document.documentElement.style.setProperty('--safe-area-top', offset + 'px');
      }
      // Helps with player panel top position, which is fine if it doesn't go up so high
      if (androidSafeAreas.top)
        document.documentElement.style.setProperty(
          '--native-safe-area-top',
          androidSafeAreas.top + 'px'
        );
    }) as EventListener);

    // Wait for IndexedDB to be ready before doing stuff with playback state
    idbStartupPromise.promise.then(() => {
      effect(() => {
        this.updateSleepTimer(sleepTimer.value);
      });

      const androidState = { state: '', requestedState: '' };
      setTimeout(() => {
        // Wait a split second for initial state to be known (may be updated by nativeState)
        effect(() => {
          const state = getState();
          const requestedState = getRequestedState();
          if (
            state !== androidState.state ||
            requestedState !== androidState.requestedState
          ) {
            // Only update Android if need be. No need to send unnecessary messages
            androidState.state = state;
            androidState.requestedState = requestedState;
            sendMessage({
              command: 'updateState',
              state,
              requestedState,
            });
          }
        });
      });

      // Let Android know what current episode's ad-skips are
      // Needs to happen in an effect() b/c ad-skips may change over time (e.g. when detected after episode is loaded)
      setTimeout(() => {
        effect(() => {
          if (currentEpisode.value) {
            this.updateAdSkips(currentEpisode.value);
          }
        });
      }, 10 * 1000);

      // Keep Android's ad-skip credits in sync with the API
      effect(() => {
        const creditsLeft = totalAdSkipsLeft.value;
        if (!Number.isFinite(creditsLeft)) return;
        sendMessage({
          command: 'updateAdSkipCreditsLeft',
          creditsLeft: Math.max(0, Math.floor(creditsLeft)),
        });
      });

      let lastSyncedNativePlaylist: NativePlaylistEpisode[] | undefined;
      effect(() => {
        if (!currentEpisodeId.value && currentPlaylist.value.length === 0) return;
        const nativePlaylist = this.getUpcomingEpisodesPayload();

        // Avoid spamming Android with unnecessary updates
        if (isEqual(lastSyncedNativePlaylist, nativePlaylist)) return;
        lastSyncedNativePlaylist = nativePlaylist;

        markUpcomingEpisodesJustSyncedToNative();
        sendMessage({
          command: 'updateUpcomingEpisodes',
          episodes: nativePlaylist,
        });
      });

      addEventListener('UPDATE_AUDIO_STATE', ((
        event: CustomEvent<{ state: AudioState }>
      ) => {
        const newState = event.detail.state;
        if (!newState) throw new Error(`Invalid state from Android: ${newState}`);
        androidState.state = newState;
        state.value = newState;
      }) as EventListener);

      addEventListener('UPDATE_SEEK', ((event: CustomEvent<{ seek: number }>) => {
        const { seek } = event.detail;
        if (typeof seek !== 'number')
          throw new Error(`Invalid seek from Android: ${seek}`);
        currentSeek.value = seek;
      }) as EventListener);

      // Doesn't appear to be used
      // addEventListener('UPDATE_DURATION', ((
      //   event: CustomEvent<{ duration: number }>
      // ) => {
      //   const { duration } = event.detail;
      //   if (typeof duration !== 'number')
      //     throw new Error(`Invalid duration from Android: ${duration}`);
      //   currentDuration.value = duration;
      // }) as EventListener);

      // Backwards compatibility - remove once new Android version is widely deployed
      addEventListener('SEEK_FROM_LAST_SESSION', ((
        event: CustomEvent<{ seek: number; timestampMs: number; episodeId: string }>
      ) => {
        // Used to set initial value for seek assuming initial episode hasn't changed
        // We store this value natively b/c occasionally the webview gets backgrounded and doesn't
        // get updated seek values even while player is playing
        exposeOnWindow({ seekFromLastSession: event.detail });
      }) as EventListener);

      // On Android, once screen has been off for 5+ minutes, setTimeout may be paused in the webview,
      // meaning that polling for the current podcast (once a minute) and polling for the current seek
      // (every second) ceases to work. In this case, the Android native app takes over and does both
      // of these things. Fortunately, it can still notify the webview to update state. I assume requests
      // will be sent once the app is once again in the foreground.
      addEventListener('MINUTE_PASSED', ((event: CustomEvent<{}>) => {
        pollForPodcastEveryMinute(true);
      }) as EventListener);

      addEventListener('AD_SKIPPED_NATIVELY', ((
        event: CustomEvent<{
          duration: number;
          // In future Android release...
          episodeId?: EpisodeCompoundId;
          startTime?: number;
          endTime?: number;
        }>
      ) => {
        const { duration, episodeId, startTime, endTime } = event.detail;
        if (episodeId && typeof startTime === 'number' && typeof endTime === 'number') {
          // New Android app version -> lookup ad by it's start time
          return trackAdSkipUnlessRecent(episodeId, startTime, endTime);
        }

        //
        // TEMPORARY workaround for Android just sending duration currently
        // TODO -> remove when no longer used
        //

        const episode = currentEpisode.peek();
        if (!episode)
          return reportSentryWarning(new Error('No episode to track ad-skip'), {
            detail: event.detail,
          });

        const matchingAds = episode.ads
          ? episode.ads.filter((ad) =>
              isWithin(ad.endTime - ad.startTime, duration, 0.1)
            )
          : [];

        if (matchingAds.length === 0)
          return reportSentryWarning(
            new Error('No matching ad found for native ad-skip'),
            {
              duration,
              episode,
            }
          );

        if (matchingAds.length > 1)
          reportSentryWarning(new Error('Multiple ads match duration'), {
            duration,
            episode,
            ads: matchingAds,
          });

        reportSentryWarning(new Error('Legacy ad-skip tracked')); // To know when we can remove this code...

        trackAdSkipUnlessRecent(
          episode.id,
          matchingAds[0].startTime,
          matchingAds[0].endTime
        );
      }) as EventListener);

      this.precacheImagesToAvoidCrash();
    });
  }

  acknowledgeAdSkipsUpTo(timestampMs: number) {
    if (!timestampMs) return;
    sendMessage({ command: 'acknowledgeAdSkipsUpTo', timestampMs });
  }

  acknowledgeUsageData({
    secondsListened,
    episodeCompletions,
  }: {
    secondsListened?: Record<EpisodeCompoundId, number>;
    episodeCompletions?: Record<EpisodeCompoundId, number>;
  }) {
    if (
      !Object.keys(secondsListened || {}).length &&
      !Object.keys(episodeCompletions || {}).length
    )
      return;
    sendMessage({
      command: 'acknowledgeUsageData',
      secondsListened,
      episodeCompletions,
    });
  }

  loadNewEpisode(episode: Episode, seek: number, playSkipAdSound = false) {
    // Set new duration
    currentDuration.value = getEpisodeDuration(episode);

    // Load new episode & update metadata
    sendMessage({
      command: 'loadNewEpisode',
      id: episode.id,
      jsonPlaylist: this.getJsonPlaylistForEpisode(episode),
      seek, // Backwards compat
      position: seek,
      playSkipAdSound,
      ads: this.getNativeAdsForEpisode(episode),
    });
    this.updateAdSkips(episode);
  }

  reset() {
    // @todo -> cancel loading and allow retry in the case of errors
    // sendMessage({ command: 'reset' });
    // this.element.src = '';
    // state.value = 'unloaded';
  }

  play() {
    sendMessage({ command: 'play' });
    // this.element.play().then(
    //   () => {
    //     state.value = 'playing';
    //   },
    //   (error) => {
    //     log('Play failed', error);
    //   }
    // );
  }

  pause() {
    sendMessage({ command: 'pause' });
    // this.element.pause();
    // state.value = 'paused';
  }

  seekTo(seconds: number, playSkipAdSound = false) {
    sendMessage({ command: 'seekTo', seconds, playSkipAdSound });
  }

  playFailToSkipAdSound() {
    sendMessage({ command: 'playFailToSkipAdSound' });
  }

  updatePlaybackRate(rate: number) {
    sendMessage({ command: 'updatePlaybackRate', rate });
  }

  updateSleepTimer(sleepTimer: SleepTimer) {
    if (isNativeAppWithStateV1)
      sendMessage({ command: 'updateSleepTimer', sleepTimer });
    else console.warn('updateSleepTimer not supported in this native app version');
  }

  async updateSeek() {
    // Ask Android for seek and receive response via event
    sendMessage({ command: 'updateSeek' });

    try {
      await waitForEvent(window, 'UPDATE_SEEK', 200);
    } catch (err) {
      reportSentryError(err as Error);
    }
  }

  //
  // Downloading audio
  //

  downloadAudioFile(episode: Episode) {
    sendMessage({
      command: 'downloadAudioFile',
      id: episode.id,
      url: episode.audioUrl,
    });
  }

  deleteAudioFile(episode: Episode) {
    sendMessage({
      command: 'deleteAudioFile',
      id: episode.id,
    });
  }

  async checkDownloadedFiles(
    getDownloadsState: () => DownloadsStore,
    getUserDataState: () => UserDataStore
  ) {
    console.log('Checking Android audio downloads...');
    sendMessage({
      command: 'getDownloadedFilesInfo',
    });

    const { detail } = (await waitForEvent(
      window,
      'DOWNLOAD_FILES_INFO',
      2500
    )) as CustomEvent<[{ id: EpisodeCompoundId; percent: number }]>;
    const downloadsOnDevice = objectFromEntries(
      detail.map(({ id, percent }) => [id, percent / 100])
    );

    // Downloads in state (should match cache)
    const { downloadsProgress, setProgress } = getDownloadsState();

    // Downloads that user has requested
    const { downloadedEpisodeIds: requestedDownloadIds } = getUserDataState();

    // For debugging
    console.log({ downloadsOnDevice, downloadsProgress, requestedDownloadIds });

    // Download any episodes that should be in cache but aren't
    // Never queue up more than 2 episodes at a time
    const stalledIds: EpisodeCompoundId[] = [];
    let queuedEpisodeCount = 0;
    requestedDownloadIds.forEach((id) => {
      if (isWithin(downloadsOnDevice[id], 1, 0.001)) {
        // Could be 1.0000001 float instead of exactly 1, sigh
        // Ensure downloadsProgress is set to 1
        if (!downloadsProgress[id] || downloadsProgress[id][0] < 1) {
          setProgress(id, 1);
        }
      } else if (
        !downloadsProgress[id] ||
        Date.now() - downloadsProgress[id][1] > 30 * 1000
      ) {
        stalledIds.push(id);
        // Episode has no progress -or- has stalled
        if (queuedEpisodeCount < 2) {
          const episode = idToEpisode(id);
          if (episode) {
            console.warn('Force download missing episode audio', id);
            this.downloadAudioFile(episode);
            queuedEpisodeCount++;
          } else {
            reportSentryError(
              new Error("Unable to download missing episode audio: can't find episode"),
              { id }
            );
          }
        }
      }
    });

    if (stalledIds.length)
      log(
        'Found',
        stalledIds.length,
        'stalled / no-progress downloads',
        stalledIds.slice(0, 5)
      );

    // Remove any persisted episodes that shouldn't be saved anymore
    objectEntries(downloadsOnDevice)
      .filter(([id]) => !requestedDownloadIds.includes(id))
      .forEach(([id]) => {
        log('Deleting file that should no longer be cached', id);
        sendMessage({ command: 'deleteAudioFile', id });
      });
  }

  //
  // Getting logs / debugging
  //

  async getNativeLogs() {
    sendMessage({ command: 'getAndroidLogs' });

    const allLogs: string[] = [];

    // Due to message length contraints, Android will send logs in chunks of 100
    const handleChunk = ((event: CustomEvent<string[]>) => {
      console.log('Received logs', event.detail.length, allLogs.length);
      allLogs.push(...event.detail);
    }) as EventListener;
    addEventListener('RECENT_LOGS_CHUNK', handleChunk);

    try {
      // Keep this slightly above Android's own timeout so native can finish and dispatch
      // completion before the browser gives up.
      await waitForEvent(window, 'RECENT_LOGS_COMPLETE', 20 * 1000);
    } catch (err) {
      log("Didn't get all logs in 20s, but returning those we did get", err);
    } finally {
      // No longer listen for chunks
      removeEventListener('RECENT_LOGS_CHUNK', handleChunk);
      return allLogs;
    }
  }

  //
  // RevenueCat user subscription
  //

  setupUserIdSync() {
    // Sync auth identity, including signed-out state, so Android can clear any
    // user-scoped native usage when the effective user changes.
    setTimeout(() => {
      effect(() => {
        const userId = currentUserId.value;
        sendMessage({ command: 'updateUserId', userId });
      });
    });
  }

  async makePurchase(offeringId: string, packageId: string) {
    sendMessage({
      command: 'makePurchase',
      offeringId,
      packageId,
    });
    // Wait for user to complete transaction for up to 15 minutes
    await waitForEvent(window, 'PURCHASE_STATUS', 15 * 60 * 1000).then((event) => {
      const result = (
        event as CustomEvent<{ transaction: unknown } | { error: unknown }>
      ).detail;
      log('Purchase result', result);
      if ('error' in result) {
        if (result.error !== 'user_cancelled') {
          toast.error(`Payment failed: ${result.error}`);
        }
      } else {
        toast.success('Payment successful! Updating your account...');
      }
      syncUsageDataNow('android-purchase');
    });
  }

  //
  // Pre-cache all album art so that if user goes offline in the future, we can
  // load the cached album art for the notification screen.
  // If album art can't be loaded, we show a plain gray square, but at least no
  // longer crash!
  //

  precacheImagesToAvoidCrash() {
    const { podcasts } = usePodcastsStore.getState();
    const imageUrls = Object.values(podcasts).map((p) => this.getPodcastImageUrl(p!));
    sendMessage({
      command: 'cacheAlbumArtImages',
      imageUrls,
    });
  }

  getPodcastImageUrl(podcast: Podcast) {
    return getThumbUrl(podcast, 600);
  }

  // Let Android know what current episode's ad-skips are so it can skip ads
  updateAdSkips(episode: Episode) {
    if (isNativeAppWithStateV1) {
      const ads = this.getNativeAdsForEpisode(episode);
      sendMessage({ command: 'updateEpisodeAds', episodeId: episode.id, ads });
    } else {
      // TODO: remove when most users are on new version
      sendMessage({
        command: 'updateAdSkips',
        ads: JSON.stringify({ episodeId: episode.id, ads: episode.ads || [] }),
      });
    }
  }

  private getUpcomingEpisodesPayload(): NativePlaylistEpisode[] {
    return currentPlaylist.value
      .map(([episodeId, position]) => {
        const episode = idToEpisode(episodeId);
        if (!episode) {
          reportSentryWarning(new Error('Playlist episode not found'), {
            episodeId,
            position,
            currentPlaylist: currentPlaylist.value,
          });
          return;
        }

        return {
          id: episode.id,
          position,
          ads: this.getNativeAdsForEpisode(episode),
          jsonPlaylist: this.getJsonPlaylistForEpisode(episode),
        };
      })
      .filter(Boolean) as NativePlaylistEpisode[];
  }

  private getJsonPlaylistForEpisode(episode: Episode) {
    return JSON.stringify({
      music: [this.getMediaItemForEpisode(episode)],
    });
  }

  private getMediaItemForEpisode(episode: Episode): UampMediaItem {
    const podcast = usePodcastsStore.getState().podcasts[episode.podcastId]!;
    return {
      id: episode.id,
      title: episode.title,
      album: episode.podcast.name,
      artist: episode.podcast.artistName,
      genre: 'Podcast',
      source: episode.audioUrl,
      image: this.getPodcastImageUrl(podcast),
      trackNumber: 1,
      totalTrackCount: 1,
      duration: Math.round(getEpisodeDuration(episode)),
      site: `${location.origin}/podcast/${podcast.id}`,
    };
  }

  private getNativeAdsForEpisode(episode: Episode) {
    return (episode.ads || []).map((ad) => ({
      ...ad,
      skipCostCredits: determineCredits(
        episode.id,
        ad.startTime,
        ad.endTime - ad.startTime
      ),
      wasRecentlySkipped: wasAdRecentlySkipped(episode.id, ad.startTime),
    }));
  }
}

export default AndroidAudioPlayer;
